import React, { useState, useEffect } from 'react';
import { Smartphone, X } from 'lucide-react';

// "Install this app" card for the customer portal (a PWA, no store needed) + a step-by-step guide for
// Android and iPhone: install, sign in, switch on notifications. Android Chrome gets a one-tap Install
// button when the browser offers it; the written steps cover everything else (and iPhone, which has no prompt).
const installed = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIos = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

const GUIDES = {
  android: {
    label: 'Android',
    install: [
      'Open this website in Chrome (not inside WhatsApp or another app).',
      'Tap the ⋮ menu at the top right of Chrome.',
      'Tap "Install app" (or "Add to Home screen"), then tap Install.',
      'The app icon now appears on your home screen. Open it from there and sign in.',
    ],
    notify: [
      'In the app, open Profile (bottom bar).',
      'Switch on "Push Notifications".',
      'When Android asks "Allow notifications?", tap Allow.',
      'Not asked, or tapped Block by mistake? Long-press the app icon > App info > Notifications > switch on.',
    ],
  },
  iphone: {
    label: 'iPhone',
    install: [
      'Open this website in Safari. Other browsers cannot install apps on iPhone. Needs iOS 16.4 or newer.',
      'Tap the Share button (square with an arrow) at the bottom of Safari.',
      'Scroll down and tap "Add to Home Screen", then tap Add.',
      'Close Safari. Open the new app icon on your home screen and sign in.',
    ],
    notify: [
      'Notifications only work when you open the app from the home screen icon, not from Safari.',
      'In the app, open Profile (bottom bar) and switch on "Push Notifications".',
      'When iPhone asks "Would like to send you notifications", tap Allow.',
      'Tapped Don\'t Allow by mistake? Settings > Notifications > this app > Allow Notifications.',
    ],
  },
};

const Steps = ({ title, items }) => (
  <>
    <div style={{ margin: '14px 0 6px', fontWeight: 700, fontSize: '.9rem', color: '#111827' }}>{title}</div>
    <ol style={{ margin: 0, paddingLeft: 20, fontSize: '.86rem', color: '#374151', lineHeight: 1.6 }}>
      {items.map(s => <li key={s} style={{ marginBottom: 6 }}>{s}</li>)}
    </ol>
  </>
);

export default function CustomerAppInstall({ link = false }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(isIos() ? 'iphone' : 'android');
  const [prompt, setPrompt] = useState(null); // Chrome's deferred install prompt, Android only

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const installNow = async () => {
    prompt.prompt();
    await prompt.userChoice.catch(() => {});
    setPrompt(null);
  };

  const g = GUIDES[tab];
  return (
    <>
      {link ? (
        <button type="button" onClick={() => setOpen(true)}
          style={{ background: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: '.85rem', fontWeight: 600, color: 'var(--primary)', padding: 8 }}>
          Install the app on your phone
        </button>
      ) : <button type="button" onClick={() => setOpen(true)}
        style={{ marginTop: link ? 0 : 14, width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: 'var(--bg-card, #fff)',
          border: '1px solid var(--border)', borderRadius: 12, cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit', textAlign: 'left' }}>
        <Smartphone size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 600, fontSize: '.95rem' }}>Install App on your phone</span>
          <span style={{ display: 'block', fontSize: '.78rem', color: '#888', marginTop: 2 }}>
            {installed() ? 'You are using the installed app' : 'Android & iPhone. Steps to install and get notifications'}
          </span>
        </span>
      </button>}
      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 18, padding: 22, width: '100%', maxWidth: 400, maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <button type="button" aria-label="Close" onClick={() => setOpen(false)}
              style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
              <X size={20} />
            </button>
            <h3 style={{ margin: '0 0 12px', fontSize: '1.1rem', fontWeight: 800, color: '#111827' }}>Install the app</h3>
            <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              {Object.entries(GUIDES).map(([k, v]) => (
                <button key={k} type="button" onClick={() => setTab(k)}
                  style={{ flex: 1, minHeight: 40, borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: '.88rem',
                    border: '1px solid var(--primary)', background: tab === k ? 'var(--primary)' : '#fff', color: tab === k ? '#fff' : 'var(--primary)' }}>
                  {v.label}
                </button>
              ))}
            </div>
            {installed() && (
              <p style={{ margin: '12px 0 0', padding: 10, borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: '.82rem' }}>
                You are already using the installed app. Jump to the notification steps below.
              </p>
            )}
            {prompt && tab === 'android' && (
              <button type="button" onClick={installNow} className="btn btn-primary"
                style={{ marginTop: 12, width: '100%', display: 'flex', justifyContent: 'center', gap: 8 }}>
                <Smartphone size={16} /> Install now
              </button>
            )}
            <Steps title="1. Install" items={g.install} />
            <Steps title="2. Turn on notifications" items={g.notify} />
          </div>
        </div>
      )}
    </>
  );
}
